import { useEffect, useState, useMemo } from "react";
import { api, API_BASE } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, RefreshCw, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const STATUSES = ["Pending", "In Progress", "Approved", "Rejected"];
const LOAN_TYPES = ["Personal Loan","Home Loan","Business Loan","MSME Loan","CV / CE Loan","Loan Against Property","CC / OD Facility"];

export default function AdminLeads() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [loanType, setLoanType] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (loanType !== "all") params.loan_type = loanType;
      if (q) params.q = q;
      const { data } = await api.get("/admin/leads", { params });
      setItems(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, loanType]);

  const counts = useMemo(() => {
    const c = { total: items.length };
    STATUSES.forEach(s => c[s] = items.filter(i => i.status === s).length);
    return c;
  }, [items]);

  const exportCSV = async () => {
    try {
      const t = localStorage.getItem("tara_token");
      const res = await fetch(`${API_BASE}/admin/leads-export`, { credentials: "include", headers: t ? { Authorization: `Bearer ${t}` } : {} });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "tara_leads.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const updateStatus = async (id, newStatus, notes) => {
    await api.patch(`/admin/leads/${id}`, { status: newStatus, notes: notes || "" });
    toast.success("Lead updated"); load();
    if (selected?.id === id) setSelected({ ...selected, status: newStatus, notes });
  };

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div><div className="overline">Dashboard</div><h1 className="font-display text-3xl font-extrabold mt-1">Lead management</h1></div>
        <div className="flex gap-2">
          <Button data-testid="leads-refresh" variant="outline" className="rounded-full" onClick={load}><RefreshCw className="h-4 w-4 mr-2"/>Refresh</Button>
          <Button data-testid="leads-export" className="btn-saffron rounded-full" onClick={exportCSV}><Download className="h-4 w-4 mr-2"/>Export CSV</Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-3">
        <KPI label="Total" value={counts.total} primary/>
        {STATUSES.map(s => <KPI key={s} label={s} value={counts[s] || 0}/>)}
      </div>

      <div className="mt-6 card-flat p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted"/>
            <Input data-testid="leads-search" value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==="Enter" && load()} placeholder="Search by name, email, mobile, PAN, agent code" className="pl-9 rounded-full"/>
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="leads-filter-status" className="sm:w-44 rounded-full"><SelectValue placeholder="All statuses"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={loanType} onValueChange={setLoanType}>
            <SelectTrigger data-testid="leads-filter-loan-type" className="sm:w-56 rounded-full"><SelectValue placeholder="All loan types"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All loan types</SelectItem>
              {LOAN_TYPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} data-testid="leads-apply-filter">Apply</Button>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead>
              <tr className="text-left text-ink-muted border-b border-slate-200">
                <th className="py-2 pr-3">Date</th>
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">Mobile</th>
                <th className="py-2 pr-3">Agent</th>
                <th className="py-2 pr-3">Loan</th>
                <th className="py-2 pr-3">Amount</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={8} className="py-10 text-center text-ink-muted">Loading…</td></tr>)}
              {!loading && items.length === 0 && (<tr><td colSpan={8} className="py-10 text-center text-ink-muted">No leads found.</td></tr>)}
              {items.map(it => (
                <tr key={it.id} className="border-b border-slate-100">
                  <td className="py-3 pr-3 whitespace-nowrap">{new Date(it.created_at).toLocaleString("en-IN", { dateStyle:"medium", timeStyle:"short" })}</td>
                  <td className="py-3 pr-3 font-medium">{it.full_name}<div className="text-xs text-ink-muted">{it.email}</div></td>
                  <td className="py-3 pr-3 font-mono text-xs">{it.mobile}</td>
                  <td className="py-3 pr-3 font-mono text-xs">{it.agent_code || <span className="text-slate-300">—</span>}</td>
                  <td className="py-3 pr-3">{it.loan_type}</td>
                  <td className="py-3 pr-3 font-semibold">₹{Number(it.loan_amount || 0).toLocaleString("en-IN")}</td>
                  <td className="py-3 pr-3">
                    <Select value={it.status} onValueChange={(v)=>updateStatus(it.id, v, it.notes || "")}>
                      <SelectTrigger data-testid={`lead-status-${it.id}`} className="h-8 rounded-full w-36"><SelectValue/></SelectTrigger>
                      <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="py-3 pr-3">
                    <Button size="sm" variant="ghost" data-testid={`lead-view-${it.id}`} onClick={()=>setSelected(it)}>View</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(v)=>!v && setSelected(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Lead details</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <Detail k="ID" v={selected.id}/>
              <Detail k="Name" v={selected.full_name}/>
              <Detail k="Email" v={selected.email}/>
              <Detail k="Mobile" v={selected.mobile}/>
              <Detail k="PAN" v={selected.pan || "—"}/>
              <Detail k="Loan Type" v={selected.loan_type}/>
              <Detail k="Amount" v={`₹${Number(selected.loan_amount || 0).toLocaleString("en-IN")}`}/>
              <Detail k="Tenure" v={`${selected.tenure_months} months`}/>
              <Detail k="Income" v={`₹${Number(selected.monthly_income || 0).toLocaleString("en-IN")}/mo`}/>
              <Detail k="Employment" v={selected.employment_type}/>
              <Detail k="Status" v={selected.status}/>
              <Detail k="Agent code" v={selected.agent_code || "—"}/>
              <div>
                <div className="text-xs uppercase tracking-wider text-ink-muted">Notes</div>
                <Textarea data-testid="lead-notes" rows={3} defaultValue={selected.notes || ""} onBlur={(e)=>updateStatus(selected.id, selected.status, e.target.value)}/>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ label, value, primary }) {
  return (
    <div className={`rounded-2xl p-4 ${primary ? "bg-slate-950 text-white" : "bg-white border border-slate-200"}`}>
      <div className={`text-[10px] uppercase tracking-[0.2em] ${primary ? "text-saffron" : "text-ink-muted"}`}>{label}</div>
      <div className="font-display font-extrabold text-2xl mt-1">{value}</div>
    </div>
  );
}

function Detail({ k, v }) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-100 pb-2 last:border-0">
      <span className="text-ink-muted">{k}</span>
      <span className="font-medium text-right break-words">{v}</span>
    </div>
  );
}
